import React, { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthProvider";
import { NetworkProvider } from "./contexts/NetworkContext"; 
import ProtectedRoute from "./components/ProtectedRoute";
import { Navigation } from "./components/Navigation";
import { BannerDisplay } from "./components/BannerDisplay";

import FlightBoardPage from "./pages/FlightBoardPage";
import AirportPage from "./pages/AirportPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUploadSchedule from "./pages/AdminUploadSchedule";
import OrganizationDashboard from "./pages/OrganizationDashboard";
import CorporatePage from "./pages/CorporatePage";
import { OrganizationLayout } from "./components/organization/OrganizationLayout";
import OrgOverviewPage from "./pages/organization/OverviewPage";
import OrgUpdateFlightsPage from "./pages/organization/UpdateFlightsPage";
import OrgAIAssistantPage from "./pages/organization/AIAssistantPage";
import OrgUploadCsvPage from "./pages/organization/UploadCsvPage";
import OrgAnnouncementsPage from "./pages/organization/AnnouncementsPage";
import OrgCitiesPage from "./pages/organization/CitiesPage";
import OrgAircraftTypesPage from "./pages/organization/AircraftTypesPage";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import OrganizationFlightsPage from "./pages/OrganizationFlightsPage";
import SecurityIncident from "./pages/SecurityIncident";
import ScheduleToolPage from "./pages/ScheduleToolPage";
import AdminIncidentReports from "./pages/AdminIncidentReports";
import UpdateFlights from "./pages/UpdateFlights";
import FastUpdate from "./pages/FastUpdate";
import { AdminLayout } from "./components/admin/AdminLayout";
import SystemOverviewPage from "./pages/admin/SystemOverviewPage";
import FlightUpdatesPage from "./pages/admin/FlightUpdatesPage";
import UsersPage from "./pages/admin/UsersPage";
import OrganizationsPage from "./pages/admin/OrganizationsPage";
import CitiesPage from "./pages/admin/CitiesPage";
import AircraftTypesPage from "./pages/admin/AircraftTypesPage";
import AdminAIAssistantPage from "./pages/admin/AIAssistantPage";
import UploadSchedulePage from "./pages/admin/UploadSchedulePage";
import BannersPage from "./pages/admin/BannersPage";
import AnnouncementsPage from "./pages/admin/AnnouncementsPage";
import ChangePasswordPage from "./pages/admin/ChangePasswordPage";
import FixUnconfirmedUsers from "./pages/admin/FixUnconfirmedUsers";
import GroundHandlingPage from "./pages/admin/GroundHandlingPage";
import PdfNormaliserPage from "./pages/tools/PdfNormaliserPage";

// Simple QueryClient with minimal configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
});

// Component to handle banner display logic with NAC logo
function BannerWrapper() {
  const location = useLocation();
  
  // Don't show banners on admin routes, login/auth routes, or the index redirect page
  const hideBannerRoutes = ['/admin', '/organization', '/login', '/signup', '/auth', '/reset-password', '/', '/flight-board'];
  const shouldShowBanner = !hideBannerRoutes.includes(location.pathname) && 
                          !location.pathname.startsWith('/admin') &&
                          !location.pathname.startsWith('/organization') &&
                          !location.pathname.startsWith('/airport/');
  
  if (!shouldShowBanner) {
    return null;
  }

  return (
    <div className="bg-white border-b">
      <div className="container mx-auto px-4 xl:px-48">
        {/* NAC Logo Section - Hidden on flight-board */}
        {location.pathname !== '/flight-board' && (
          <div className="py-4 flex justify-center">
            <div className="text-center">
              <img 
                src="/lovable-uploads/e3ef3c38-0fe3-4c62-b2aa-7d370e618811.png" 
                alt="NAC - National Airports Corporation" 
                className="h-16 w-auto mx-auto mb-2"
              />
            </div>
          </div>
        )}
        
        {/* Banner Display Section */}
        <div className="pb-4">
          <BannerDisplay />
        </div>
      </div>
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  const isFastUpdate = location.pathname === '/FastUpdate';

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {!isFastUpdate && <Navigation />}
      {!isFastUpdate && <BannerWrapper />}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/flight-board" element={<FlightBoardPage />} />
          <Route path="/airport/:airportCode" element={<AirportPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Admin Routes with Sidebar */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'administrator']}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SystemOverviewPage />} />
            <Route path="overview" element={<SystemOverviewPage />} />
            <Route path="flight-updates" element={<FlightUpdatesPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="cities" element={<CitiesPage />} />
            <Route path="aircraft-types" element={<AircraftTypesPage />} />
            <Route path="upload-schedule" element={<UploadSchedulePage />} />
            <Route path="banners" element={<BannersPage />} />
            <Route path="announcements" element={<AnnouncementsPage />} />
            <Route path="change-password" element={<ChangePasswordPage />} />
            <Route path="ground-handling" element={<GroundHandlingPage />} />
            <Route path="fix-users" element={<FixUnconfirmedUsers />} />
          </Route>
          
          {/* Organization Routes with Sidebar */}
          <Route 
            path="/organization" 
            element={
              <ProtectedRoute allowedRoles={['organization', 'organization_user']}>
                <OrganizationLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<OrgOverviewPage />} />
            <Route path="update-flights" element={<OrgUpdateFlightsPage />} />
            <Route path="upload-csv" element={<OrgUploadCsvPage />} />
            <Route path="announcements" element={<OrgAnnouncementsPage />} />
            <Route path="cities" element={<OrgCitiesPage />} />
            <Route path="aircraft-types" element={<OrgAircraftTypesPage />} />
          </Route>
          <Route 
            path="/UpdateFlights" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'administrator', 'organization', 'organization_user']}>
                <UpdateFlights />
              </ProtectedRoute>
            } 
          />
          <Route path="/FastUpdate" element={<FastUpdate />} />
          <Route 
            path="/admin/incidentreport" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'administrator']}>
                <AdminIncidentReports />
              </ProtectedRoute>
            } 
          />
          <Route path="/corporate" element={<CorporatePage />} />
          <Route path="/schedule-tool" element={<ScheduleToolPage />} />
          <Route path="/tools/pdf-normaliser" element={<PdfNormaliserPage />} />
          <Route path="/securityincident" element={<SecurityIncident />} />
          <Route path="/:orgCode" element={<OrganizationFlightsPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isFastUpdate && (
        <footer className="py-6 border-t bg-white">
          <div className="container mx-auto px-4 text-center">
            <div className="text-sm text-foreground space-y-2">
              <p>© {new Date().getFullYear()} National Airports Corporation. All rights reserved.</p>
              <p>A flight information data system for PNG Civil Aviation.</p>
              <p className="mt-3">
                <Link 
                  to="/securityincident" 
                  className="text-primary hover:text-primary/80 hover:underline transition-colors"
                >
                  Report any suspicious, unsafe or illegal practice you witness here
                </Link>
              </p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <NetworkProvider>
          <AuthProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </AuthProvider>
        </NetworkProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;